import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", booking: null, error: "" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", booking: null, error: "Missing session reference." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/bookings/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", booking: data.booking, error: "" });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", booking: null, error: "Payment did not complete." });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ phase: "pending", booking: null, error: "Still processing — we'll email you once confirmed." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", booking: null, error: "Unable to verify payment." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[60vh] container-x section-y flex items-center bg-ink" data-testid="booking-success-page">
      <div className="max-w-2xl mx-auto card-brutal text-center w-full">
        {state.phase === "checking" && (
          <>
            <Loader2 size={36} className="mx-auto text-rose animate-spin" />
            <p className="mt-5 font-display text-3xl uppercase text-white">Confirming payment…</p>
            <p className="mt-3 text-white/70">Hang tight — we're checking with Stripe.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-lime" />
            <p className="mt-5 font-display text-5xl uppercase text-white">Booking confirmed</p>
            <p className="mt-3 text-white/80 text-lg">
              Thank you{state.booking?.parent_name ? `, ${state.booking.parent_name}` : ""} —
              your deposit's in and {state.booking?.child_name ? `${state.booking.child_name}'s` : "your"} spot is locked.
            </p>
            {state.booking && (
              <div className="mt-8 text-left bg-ink border-2 border-white/20 p-5 text-sm space-y-1.5">
                <div><span className="text-gold uppercase tracking-wider text-[11px]">Workshop:</span> <span className="text-white">{state.booking.workshop_type}</span></div>
                <div><span className="text-gold uppercase tracking-wider text-[11px]">Date:</span> <span className="text-white">{state.booking.preferred_date || "TBC"}</span></div>
                <div><span className="text-gold uppercase tracking-wider text-[11px]">Attendees:</span> <span className="text-white">{state.booking.participants}</span></div>
                <div><span className="text-gold uppercase tracking-wider text-[11px]">Deposit paid:</span> <span className="text-rose font-bold">£{Number(state.booking.deposit_amount).toFixed(2)}</span></div>
              </div>
            )}
            <p className="mt-6 text-sm text-white/60">
              We'll be in touch within 48 hours. Remaining balance is paid on the day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/jacket-questionnaire" className="btn-accent" data-testid="success-questionnaire-btn">
                Tell us about your jacket vision
              </Link>
              <Link to="/" className="btn-secondary">Back to home</Link>
            </div>
          </>
        )}
        {state.phase === "failed" && (
          <>
            <XCircle size={42} className="mx-auto text-rose" />
            <p className="mt-5 font-display text-3xl uppercase text-white">Payment didn't go through</p>
            <p className="mt-3 text-white/70">No booking was saved — try again, your details aren't lost.</p>
            <Link to="/workshops" className="mt-6 btn-accent inline-flex">Try again</Link>
          </>
        )}
        {state.phase === "pending" && (
          <>
            <Loader2 size={36} className="mx-auto text-gold" />
            <p className="mt-5 font-display text-3xl uppercase text-white">Still processing…</p>
            <p className="mt-3 text-white/70">{state.error}</p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle size={42} className="mx-auto text-rose" />
            <p className="mt-5 font-display text-3xl uppercase text-white">Something went wrong</p>
            <p className="mt-3 text-white/70">{state.error || "Please contact us if you were charged."}</p>
            <Link to="/contact" className="mt-6 btn-accent inline-flex">Contact us</Link>
          </>
        )}
      </div>
    </div>
  );
}
